'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { API_BASE, COMPANY_ID, browserCanReachKit } from '@/lib/kit-client';
import { visitorId } from '@/lib/visitor-id';

/**
 * Visitor analytics and heatmap collection for this site.
 *
 * Both modules were switched on in KIT and nothing was sending to them, so the
 * owner's Visitors and Heatmaps dashboards sat empty. These are the same two
 * beacons KIT's own hosted sites use, with one addition: KIT's version reads a
 * `kit_vid` cookie that its own middleware sets, and nothing sets that here, so
 * we mint it ourselves. Without it every hit is anonymous and "unique
 * visitors" can never be more than a guess.
 *
 * Delivery is fire-and-forget through sendBeacon, which is built to survive the
 * page being unloaded mid-request - a plain fetch gets cancelled exactly when
 * these fire, as the visitor leaves. Nothing here throws, blocks navigation, or
 * collects anything identifying: a random id, a path, a referrer, and click and
 * scroll positions as percentages.
 *
 * The key travels in the JSON body, not a header, because sendBeacon cannot set
 * headers. The API accepts either.
 */

const API_KEY = process.env.NEXT_PUBLIC_KIT_API_KEY || '';

function send(url: string, payload: unknown) {
  const body = JSON.stringify(payload);
  try {
    if (typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      return;
    }
    void fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* telemetry must never surface to the visitor */
  }
}

function viewportBucket(): 'mobile' | 'tablet' | 'desktop' {
  const w = window.innerWidth;
  if (w < 640) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

/** One page view per navigation. Referrer is only meaningful on the first. */
function PageViews() {
  const pathname = usePathname();
  const isFirst = useRef(true);

  useEffect(() => {
    if (!API_KEY || !browserCanReachKit()) return;
    const vid = visitorId();
    // No visitor id means analytics is not allowed for this visitor (consent required and not given).
    if (vid) {
      send(`${API_BASE}/api/${COMPANY_ID}/analytics/pageview`, {
        apiKey: API_KEY,
        path: pathname,
        visitorId: vid,
        referrer: isFirst.current ? document.referrer : '',
      });
    }
    isFirst.current = false;
  }, [pathname]);

  return null;
}

type Sample = { type: 'click' | 'scroll'; xPct: number; yPct: number };

/**
 * Clicks and furthest scroll depth, batched. Coordinates are percentages of
 * the whole document rather than pixels, so a click on a phone and the same
 * click on a desktop land in the same place when the owner views the heatmap.
 */
function Heatmap() {
  const pathname = usePathname();
  const buffer = useRef<Sample[]>([]);
  const maxScroll = useRef(0);

  useEffect(() => {
    if (!API_KEY || !browserCanReachKit()) return;
    const url = `${API_BASE}/api/${COMPANY_ID}/heatmap`;
    maxScroll.current = 0;

    const docSize = () => {
      const el = document.documentElement;
      return {
        width: Math.max(el.scrollWidth, el.clientWidth, 1),
        height: Math.max(el.scrollHeight, el.clientHeight, 1),
      };
    };

    function flush(withScroll: boolean) {
      const events = buffer.current.slice();
      buffer.current = [];
      if (withScroll && maxScroll.current > 0) {
        events.push({ type: 'scroll', xPct: 0, yPct: maxScroll.current });
      }
      // The API rejects an empty batch, so do not send one.
      if (events.length === 0) return;
      const vid = visitorId();
      if (!vid) return; // analytics not allowed for this visitor

      send(url, {
        apiKey: API_KEY,
        path: pathname,
        viewport: viewportBucket(),
        visitorId: vid,
        events: events.slice(0, 50), // the API caps a batch at 50
      });
    }

    function onClick(e: MouseEvent) {
      const { width, height } = docSize();
      const x = ((e.pageX ?? e.clientX + window.scrollX) / width) * 100;
      const y = ((e.pageY ?? e.clientY + window.scrollY) / height) * 100;
      buffer.current.push({ type: 'click', xPct: x, yPct: y });
      // Flush rather than let a rapid-clicking session grow past the cap.
      if (buffer.current.length >= 20) flush(false);
    }

    function onScroll() {
      const { height } = docSize();
      const reached = ((window.scrollY + window.innerHeight) / height) * 100;
      if (reached > maxScroll.current) maxScroll.current = Math.min(100, reached);
    }

    function onVisibility() {
      if (document.visibilityState === 'hidden') flush(true);
    }

    function onPageHide() {
      flush(true);
    }

    document.addEventListener('click', onClick, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);

    return () => {
      flush(true); // includes this page's final scroll depth
      document.removeEventListener('click', onClick);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [pathname]);

  return null;
}

export function SiteAnalytics({
  visitorAnalytics = false,
  heatmaps = false,
}: {
  visitorAnalytics?: boolean;
  heatmaps?: boolean;
}) {
  return (
    <>
      {visitorAnalytics && <PageViews />}
      {heatmaps && <Heatmap />}
    </>
  );
}
